// ============================================================================
// ProbSolver v3.0 — App Router
// ============================================================================
//
// ROUTING ARCHITECTURE:
//
// 1. Public routes: Auth pages — no protection, accessible to anyone.
//
// 2. Auth-only routes: Onboarding and password change — require login
//    but NOT team context (user hasn't chosen a team yet).
//
// 3. Main app routes: Require authentication + completed onboarding +
//    active team context. Wrapped in AppShell (sidebar + topbar).
//
// 4. Super Admin routes: Require SUPER_ADMIN globalRole. Separate
//    layout section so SUPER_ADMIN can access platform tools without
//    a team context.
//
// 5. Lazy loading: Heavy pages (MockInterview, Excalidraw, Showcase)
//    are lazy-loaded to keep the initial bundle small. Suspense
//    provides a loading fallback while chunks download.
//
// ============================================================================
import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { MotionConfig } from 'framer-motion'
import { ErrorBoundary } from '@components/ui/ErrorBoundary'
import { ConfirmProvider } from '@components/ui/ConfirmModal'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
// ── Layout ───────────────────────────────────────────────────
import { AppShell } from '@components/layout/AppShell'
import ProtectedRoute from '@components/layout/ProtectedRoute'
import { Spinner } from '@components/ui/Spinner'
import { ToastContainer } from '@components/ui/Toast'
import useAuthStore from '@store/useAuthStore'
// ── Auth pages (always eager — small bundle) ─────────────────
import Login from '@pages/auth/Login'
import Register from '@pages/auth/Register'
import VerifyEmailPage from '@pages/auth/VerifyEmailPage'
import ForgotPasswordPage from '@pages/auth/ForgotPasswordPage'
import ResetPasswordPage from '@pages/auth/ResetPasswordPage'
import ChangePasswordPage from '@pages/auth/ChangePasswordPage'
// ── Onboarding (eager — first thing new users see) ───────────
import OnboardingPage from '@pages/OnboardingPage'
// ── Landing pages (eager — first things cold visitors see) ───
import LandingPage from '@pages/landing/LandingPage'
import AboutPage from '@pages/landing/AboutPage'
import ContactPage from '@pages/landing/ContactPage'
// ── Core pages (eager — frequently accessed) ─────────────────
import Dashboard from '@pages/Dashboard'
import LeaderboardPage from '@pages/LeaderboardPage'
import ReportPage from '@pages/ReportPage'
import ReviewQueuePage from '@pages/ReviewQueuePage'
import QuizPage from '@pages/QuizPage'
import QuizReviewPage from '@pages/QuizReviewPage'
import ProfilePage from '@pages/ProfilePage'
import SettingsPage from '@pages/SettingsPage'
import InterviewHistoryPage from '@pages/InterviewHistoryPage'
// ── Team pages (eager — critical for onboarding flow) ────────
import TeamManagePage from '@pages/team/TeamManagePage'
import TeamPendingPage from '@pages/team/TeamPendingPage'
// ── Super Admin (eager — only loaded for SUPER_ADMIN) ────────
import SuperAdminDashboard from '@pages/superadmin/SuperAdminDashboard'
// ── Heavy pages (lazy — loaded on demand) ────────────────────
const MockInterviewPage = lazy(() => import('@pages/MockInterviewPage'))
const TeachingListPage = lazy(() => import('@pages/teaching/TeachingListPage'))
const TeachingNewPage = lazy(() => import('@pages/teaching/TeachingNewPage'))
const TeachingDetailPage = lazy(() => import('@pages/teaching/TeachingDetailPage'))
const TeachingNotesPage = lazy(() => import('@pages/teaching/TeachingNotesPage'))
const TeachingFlagsPage = lazy(() => import('@pages/superadmin/TeachingFlagsPage'))
const NotesListPage = lazy(() => import('@pages/notes/NotesListPage'))
const NoteNewPage = lazy(() => import('@pages/notes/NoteNewPage'))
const NoteDetailPage = lazy(() => import('@pages/notes/NoteDetailPage'))
const LearnPage = lazy(() => import('@pages/learn/LearnPage'))
const TopicDetailPage = lazy(() => import('@pages/learn/TopicDetailPage'))
const CalibrationPage = lazy(() => import('@pages/learn/CalibrationPage'))
const ConceptPage = lazy(() => import('@pages/learn/ConceptPage'))
const SuperAdminDiagnosticsPage = lazy(() => import('@pages/superadmin/SuperAdminDiagnosticsPage'))
const DesignStudioPage = lazy(() => import('@pages/DesignStudioPage'))
// ── Admin pages (lazy — only TEAM_ADMIN accesses these) ──────
const AdminPage = lazy(() => import('@pages/admin/AdminPage'))
const AddProblemPage = lazy(() => import('@pages/admin/AddProblemPage'))
const DesignReferencesAdmin = lazy(() => import('@pages/admin/DesignReferencesAdmin'))
const EditProblemPage = lazy(() => import('@pages/admin/EditProblemPage'))
const ProductHealthPage = lazy(() => import('@pages/admin/ProductHealthPage'))
const ShowcasePage = lazy(() => import('@pages/admin/showcase/ShowcasePage'))
// ── Curriculum admin (lazy — TEAM_ADMIN, gated by VITE_FEATURE_CURRICULUM) ─
const CurriculumAdminPage = lazy(() => import('@pages/team-admin/curriculum/CurriculumAdminPage'))
const TemplateBrowserPage = lazy(() => import('@pages/team-admin/curriculum/TemplateBrowserPage'))
const TopicAuthoringPage  = lazy(() => import('@pages/team-admin/curriculum/TopicAuthoringPage'))
// ── Docs (lazy — rarely accessed) ────────────────────────────
const ReadmePage = lazy(() => import('@pages/docs/ReadmePage'))
const SetupPage = lazy(() => import('@pages/docs/SetupPage'))
const DeployPage = lazy(() => import('@pages/docs/DeployPage'))
const HowToPage = lazy(() => import('@pages/docs/howto/HowToShell'))
const HowToTaskPage = lazy(() => import('@pages/docs/howto/TaskPage'))
const SelectTeamPage = lazy(() => import('@pages/SelectTeamPage'))
const ProblemsPage = lazy(() => import('@pages/problems/ProblemsPage'))
const ProblemDetailPage = lazy(() => import('@pages/problems/ProblemDetailPage'))
const SubmitSolutionPage = lazy(() => import('@pages/problems/SubmitSolutionPage'))
const EditSolutionPage = lazy(() => import('@pages/problems/EditSolutionPage'))
const SolutionHistoryPage = lazy(() => import('@pages/problems/SolutionHistoryPage'))
const AllTeamsPage = lazy(() => import('@pages/superadmin/AllTeamsPage'))
const AllUsersPage = lazy(() => import('@pages/superadmin/AllUsersPage'))
const SuperAdminAnalyticsPage = lazy(() => import('@pages/superadmin/SuperAdminAnalyticsPage'))
const TodoPage = lazy(() => import('@pages/superadmin/TodoPage'))
const FeedbackPage = lazy(() => import('@pages/FeedbackPage'))
const FeedbackInboxPage = lazy(() => import('@pages/superadmin/FeedbackInboxPage'))
const VerdictsAuditPage = lazy(() => import('@pages/superadmin/VerdictsAuditPage'))
const AIUsagePage = lazy(() => import('@pages/superadmin/AIUsagePage'))

// ============================================================================
// QUERY CLIENT
// ============================================================================
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,       // 2 minutes
      gcTime: 1000 * 60 * 10,         // 10 minutes (garbage collection)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})
// ============================================================================
// SUSPENSE FALLBACK
// ============================================================================
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-[60vh]">
      <div className="flex flex-col items-center gap-3">
        <Spinner size="lg" />
        <p className="text-xs text-text-tertiary">Loading...</p>
      </div>
    </div>
  )
}
// Wraps lazy-loaded routes in Suspense + ErrorBoundary so a crash on
// any one page renders a recoverable fallback INSIDE the AppShell
// layout (sidebar / topbar stay), instead of taking down the whole app.
function Lazy({ children }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>{children}</Suspense>
    </ErrorBoundary>
  )
}
// ── Catch-all: redirect based on role ────────────────────────
function CatchAllRedirect() {
  const { user, isAuthenticated } = useAuthStore()
  if (!isAuthenticated) return <Navigate to="/" replace />
  if (user?.globalRole === 'SUPER_ADMIN') return <Navigate to="/super-admin" replace />
  return <Navigate to="/dashboard" replace />
}
// ============================================================================
// APP
// ============================================================================
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Honor the user's `prefers-reduced-motion: reduce` setting across
          every framer-motion animation in the app — entrance fades,
          stagger, drawers, etc. The `"user"` setting reads the OS-level
          preference; users with motion sensitivity get instant
          transitions instead of animated ones. Pairs with Tailwind's
          `motion-safe:` / `motion-reduce:` variants used in Skeleton. */}
      <MotionConfig reducedMotion="user">
      <ConfirmProvider>
      <BrowserRouter>
        <Routes>
          {/* ============================================================ */}
          {/* PUBLIC ROUTES — No authentication required                   */}
          {/* ============================================================ */}
          {/* Landing + marketing pages — public + always viewable.
              Authed users see the same chrome with a "Go to Dashboard" CTA
              instead of "Sign In + Start Free" (LandingNav reads auth state). */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/auth/login" element={<Login />} />
          <Route path="/auth/register" element={<Register />} />
          <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
          {/* ============================================================ */}
          {/* AUTH-ONLY ROUTES — Logged in but no team context required    */}
          {/* These are for users who haven't completed onboarding yet,   */}
          {/* or need to change their password before accessing the app.  */}
          {/* ============================================================ */}
          <Route
            path="/auth/change-password"
            element={
              <ProtectedRoute>
                <ChangePasswordPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />
          {/* ── SUPER_ADMIN team picker (outside AppShell — sidebar/topbar hidden) */}
          <Route
            path="/select-team"
            element={
              <ProtectedRoute requireSuperAdmin>
                <Lazy><SelectTeamPage /></Lazy>
              </ProtectedRoute>
            }
          />
          {/* ============================================================ */}
          {/* SUPER ADMIN ROUTES — Platform-level management              */}
          {/* Wrapped in AppShell for consistent layout.                  */}
          {/* SUPER_ADMIN may not have a team context — that's OK.        */}
          {/* These routes only check globalRole, not team.               */}
          {/* ============================================================ */}
          <Route
            element={
              <ProtectedRoute requireSuperAdmin>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route path="/super-admin" element={<SuperAdminDashboard />} />
            <Route path="/super-admin/teams" element={<Lazy><AllTeamsPage /></Lazy>} />
            <Route path="/super-admin/users" element={<Lazy><AllUsersPage /></Lazy>} />
            <Route path="/super-admin/analytics" element={<Lazy><SuperAdminAnalyticsPage /></Lazy>} />
            <Route path="/super-admin/feedback" element={<Lazy><FeedbackInboxPage /></Lazy>} />
            <Route path="/super-admin/verdicts" element={<Lazy><VerdictsAuditPage /></Lazy>} />
            <Route path="/super-admin/ai-usage" element={<Lazy><AIUsagePage /></Lazy>} />
            <Route path="/super-admin/diagnostics" element={<Lazy><SuperAdminDiagnosticsPage /></Lazy>} />
            {import.meta.env.VITE_FEATURE_TEACHING_SESSIONS === 'true' && (
              <Route path="/super-admin/teaching-flags" element={<Lazy><TeachingFlagsPage /></Lazy>} />
            )}
            {/* SuperAdmin also needs profile + settings within their layout */}
            <Route path="/super-admin/profile/:userId" element={<ProfilePage />} />
            <Route path="/super-admin/profile" element={<ProfilePage />} />
            <Route path="/super-admin/settings" element={<SettingsPage />} />
            <Route path="/super-admin/showcase" element={<Lazy><ShowcasePage /></Lazy>} />
            <Route path="/super-admin/roadmap" element={<Lazy><TodoPage /></Lazy>} />
          </Route>
          {/* ============================================================ */}
          {/* MAIN APP ROUTES — Require auth + onboarding + team context  */}
          {/* Every page inside here can rely on req.teamId being set.    */}
          {/* The AppShell provides sidebar (with team switcher) + topbar */}
          {/* ============================================================ */}
          <Route
            element={
              <ProtectedRoute requireTeamContext>
                <AppShell />
              </ProtectedRoute>
            }
          >
            {/* ── Dashboard ─────────────────────────────────────────── */}
            <Route path="dashboard" element={<Dashboard />} />
            {/* ── Problems ──────────────────────────────────────────── */}
            <Route path="problems" element={<Lazy><ProblemsPage /></Lazy>} />
            <Route path="problems/:problemId" element={<Lazy><ProblemDetailPage /></Lazy>} />
            <Route path="problems/:problemId/submit" element={<Lazy><SubmitSolutionPage /></Lazy>} />
            <Route path="problems/:problemId/edit-solution/:solutionId" element={<Lazy><EditSolutionPage /></Lazy>} />
            <Route path="solutions/:solutionId/history" element={<Lazy><SolutionHistoryPage /></Lazy>} />
            {/* ── Solutions & Review ────────────────────────────────── */}
            <Route path="review" element={<ReviewQueuePage />} />
            {/* ── Quizzes ───────────────────────────────────────────── */}
            <Route path="quizzes" element={<QuizPage />} />
            <Route path="quizzes/:quizId/review" element={<QuizReviewPage />} />
            {/* ── Mock Interview (lazy — heavy: Excalidraw + WS) ────── */}
            <Route path="mock-interview" element={<Lazy><MockInterviewPage /></Lazy>} />
            {/* ── Design Studio (lazy — heavy: Excalidraw + AI) ─────── */}
            <Route path="design-studio" element={<Lazy><DesignStudioPage /></Lazy>} />
            {/* ── Interview History ─────────────────────────────────── */}
            <Route path="interview-history" element={<InterviewHistoryPage />} />
            {/* ── Team Teaching (gated — VITE_FEATURE_TEACHING_SESSIONS) */}
            {import.meta.env.VITE_FEATURE_TEACHING_SESSIONS === 'true' && (
              <>
                <Route path="teaching" element={<Lazy><TeachingListPage /></Lazy>} />
                <Route path="teaching/new" element={<Lazy><TeachingNewPage /></Lazy>} />
                <Route path="teaching/:id" element={<Lazy><TeachingDetailPage /></Lazy>} />
                <Route path="teaching/:id/notes" element={<Lazy><TeachingNotesPage /></Lazy>} />
              </>
            )}
            {/* ── Personal Notes (gated — VITE_FEATURE_NOTES_ENABLED) ── */}
            {import.meta.env.VITE_FEATURE_NOTES_ENABLED === 'true' && (
              <>
                <Route path="notes" element={<Lazy><NotesListPage /></Lazy>} />
                <Route path="notes/new" element={<Lazy><NoteNewPage /></Lazy>} />
                <Route path="notes/:id" element={<Lazy><NoteDetailPage /></Lazy>} />
              </>
            )}
            {/* ── Learn — team-scoped published curricula (gated) ────
                Whole `/learn/*` subtree registers only when
                VITE_FEATURE_CURRICULUM === 'true'. Server responses are
                team-scoped + PUBLISHED-only; if a team has no published
                topics the catalog renders an empty state without error.
                Flag OFF → routes drop out → hand-typed URL 404s. */}
            {import.meta.env.VITE_FEATURE_CURRICULUM === 'true' && (
              <>
                <Route path="learn" element={<Lazy><LearnPage /></Lazy>} />
                <Route path="learn/:slug" element={<Lazy><TopicDetailPage /></Lazy>} />
                <Route path="learn/:slug/calibration" element={<Lazy><CalibrationPage /></Lazy>} />
                <Route path="learn/:slug/concepts/:conceptSlug" element={<Lazy><ConceptPage /></Lazy>} />
              </>
            )}
            {/* ── Leaderboard ───────────────────────────────────────── */}
            <Route path="leaderboard" element={<LeaderboardPage />} />
            {/* ── Intelligence Report ───────────────────────────────── */}
            <Route path="report" element={<ReportPage />} />
            {/* ── Feedback ──────────────────────────────────────────── */}
            <Route path="feedback" element={<Lazy><FeedbackPage /></Lazy>} />
            {/* ── Profile ───────────────────────────────────────────── */}
            <Route path="profile" element={<ProfilePage />} />
            <Route path="profile/:userId" element={<ProfilePage />} />
            {/* ── Settings ──────────────────────────────────────────── */}
            <Route path="settings" element={<SettingsPage />} />
            {/* ── Team Management ───────────────────────────────────── */}
            <Route path="team" element={<TeamManagePage />} />
            <Route path="team/pending" element={<TeamPendingPage />} />
            {/* ── Team Admin Routes (TEAM_ADMIN or SUPER_ADMIN) ────── */}
            <Route
              path="admin"
              element={
                <ProtectedRoute requireTeamAdmin>
                  <Lazy><AdminPage /></Lazy>
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/add-problem"
              element={
                <ProtectedRoute requireTeamAdmin>
                  <Lazy><AddProblemPage /></Lazy>
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/edit-problem/:problemId"
              element={
                <ProtectedRoute requireTeamAdmin>
                  <Lazy><EditProblemPage /></Lazy>
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/design-references"
              element={
                <ProtectedRoute requireTeamAdmin>
                  <Lazy><DesignReferencesAdmin /></Lazy>
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/analytics"
              element={
                <ProtectedRoute requireTeamAdmin>
                  <Lazy><ProductHealthPage /></Lazy>
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/showcase"
              element={
                <ProtectedRoute requireTeamAdmin>
                  <Lazy><ShowcasePage /></Lazy>
                </ProtectedRoute>
              }
            />
            {/* ── Curriculum Admin (TEAM_ADMIN, gated) ─────────────── */}
            {/* Feature-flag gate: FF-OFF → routes are not registered → 404.
                Server also gates the /curriculum/admin/* API surface behind
                FEATURE_CURRICULUM, so a hand-typed URL with the flag off
                would also fail at the network layer. Note: a SUPER_ADMIN
                template-curation surface will land in Phase 2; today the
                SYNC endpoint is admin-only via CLI (npm run curriculum:sync). */}
            {import.meta.env.VITE_FEATURE_CURRICULUM === 'true' && (
              <>
                <Route
                  path="admin/curriculum"
                  element={
                    <ProtectedRoute requireTeamAdmin>
                      <Lazy><CurriculumAdminPage /></Lazy>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/curriculum/templates"
                  element={
                    <ProtectedRoute requireTeamAdmin>
                      <Lazy><TemplateBrowserPage /></Lazy>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/curriculum/topics/:id"
                  element={
                    <ProtectedRoute requireTeamAdmin>
                      <Lazy><TopicAuthoringPage /></Lazy>
                    </ProtectedRoute>
                  }
                />
              </>
            )}
            {/* ── Documentation (lazy) ──────────────────────────────── */}
            <Route path="docs/readme" element={<Lazy><ReadmePage /></Lazy>} />
            <Route path="docs/setup" element={<Lazy><SetupPage /></Lazy>} />
            <Route path="docs/deploy" element={<Lazy><DeployPage /></Lazy>} />
            <Route path="docs/how-to" element={<Lazy><HowToPage /></Lazy>} />
            <Route path="docs/how-to/task/:taskId" element={<Lazy><HowToTaskPage /></Lazy>} />
          </Route>
          {/* ============================================================ */}
          {/* CATCH-ALL — Redirect unknown routes to home                 */}
          {/* ============================================================ */}
          <Route path="*" element={<CatchAllRedirect />} />
        </Routes>
      </BrowserRouter>
      <ToastContainer />
      </ConfirmProvider>
      </MotionConfig>
    </QueryClientProvider>
  )
}